import SplitType from "split-type";
import { gsap, Power1 } from "gsap";

const duration = 1;
const dataMap = new WeakMap<Object>();
let observer: IntersectionObserver | null = null;

const initObserver = () => {
  if (observer) return;
  observer = new IntersectionObserver(
    (entries) => {
      entries.forEach(({ target, isIntersecting }) => {
        if (isIntersecting) {
          const data = dataMap.get(target);
          if (data?.timeline) data.timeline.play();
          observer?.unobserve(target);
        }
      });
    },
    {
      rootMargin: "0px 0px -30px 0px",
    }
  );
};

const initAnimation = (el: HTMLElement) => {
  const data = {
    timeline: undefined as GSAPTimeline | undefined,
    findElem: undefined as HTMLElement | undefined,
    splitInstance: undefined as SplitType | undefined,
    destroy: (() => {}) as () => void,
  };

  const store = useLayoutStore();
  const IS_Mobile = computed(() => store.IS_Mobile);
  const IS_Tablet = computed(() => store.IS_Tablet);
  const IS_Tablet2 = computed(() => store.IS_Tablet2);

  const currentSize = computed(() => {
    if (IS_Mobile.value) return "mobile";
    if (IS_Tablet2.value) return "tablet2";
    if (IS_Tablet.value) return "tablet";
    return "desktop";
  });

  watch(currentSize, (value) => {
    data.destroy();
    nextTick(() => initAnimation(el));
  });

  const type = el.getAttribute("data-fade-element");
  const delay = parseInt(el.getAttribute("data-fade-delay") || "0");
  const shift = parseInt(el.getAttribute("data-fade-shift") || "70");
  const viewport = el.getAttribute("data-fade-viewport") || "desktop"; // mobile, tablet, tablet2, desktop

  data.timeline = gsap.timeline({ paused: true });

  const canInitAnimation = (() => {
    if (viewport === "mobile") return IS_Mobile.value;
    if (viewport === "tablet2") return IS_Mobile.value || IS_Tablet2.value;
    if (viewport === "tablet")
      return IS_Mobile.value || IS_Tablet2.value || IS_Tablet.value;
    return true;
  })();

  if (canInitAnimation) {
    if (type === "title" || type === "tag") {
      const findElem = (() => {
        if (el.querySelector("h1,h2,h3,.text"))
          return el.querySelector("h1,h2,h3,.text");
        return el;
      })();

      data.findElem = findElem as HTMLElement;

      const staggerComputed = (() => {
        if (el.getAttribute("data-fade-timeline") === "slow") return 0.1;
        return type === "tag" ? 0.25 : 0.055;
      })();

      data.splitInstance = new SplitType(data.findElem, {
        types: "lines,words",
        lineClass: "line",
      });
      data.timeline.from(el.querySelectorAll(".line .word"), {
        yPercent: 100,
        duration: duration,
        delay: delay / 1000,
        ease: Power1.easeOut,
        stagger: staggerComputed,
        clearProps: "yPercent",
        willChange: "transform, opacity",
        force3D: true,
        autoRound: false,
      });
      data.destroy = () => {
        const { splitInstance } = dataMap.get(el);
        splitInstance?.revert();
        if (data.findElem) {
          gsap.set(data.findElem, { clearProps: "all" });
        }
      };
    } else {
      data.timeline.fromTo(
        el,
        { y: shift, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: duration,
          delay: delay / 1000,
          ease: Power1.easeOut,
          clearProps: "y, opacity",
          willChange: "transform, opacity",
          force3D: true,
          autoRound: false,
        }
      );
      data.destroy = () => {
        if (data.findElem) {
          gsap.set(data.findElem, { clearProps: "all" });
        }
      };
    }
    observer?.observe(el);
  }

  dataMap.set(el, data);
  el.classList.add("init");
};

export default {
  mounted(el: HTMLElement) {
    if (!observer) initObserver();
    if (document.fonts) {
      document.fonts.ready.then(() => {
        nextTick(() => initAnimation(el));
      });
    } else {
      nextTick(() => initAnimation(el));
    }
  },
  unmounted(el: HTMLElement) {
    el.classList.remove("init");
    const data = dataMap.get(el);
    if (!data) return;
    const { timeline, splitInstance } = data;
    if (timeline) timeline.kill();
    if (splitInstance) splitInstance.revert();
    observer?.unobserve(el);
  },
};
